import {
  ISelectOption,
  SingleSelect,
} from 'crono-fe-common/components/SingleSelect';
import MicrophoneIcon from 'crono-fe-common/icons/Icon-Microphone';
import SpeakerIcon from 'crono-fe-common/icons/SpeakerIcon';
import { colors } from 'crono-fe-common/theme';
import { useEffect, useRef, useMemo } from 'react';
import { CronoCallState, ClosedState } from './hooks/cronoCallReducer';

export const Settings = ({
  callState,
  close,
  changeMicrophone,
  changeSpeaker,
}: {
  callState: Exclude<CronoCallState, ClosedState>;
  close: () => void;
  changeMicrophone: (deviceId: string) => void;
  changeSpeaker: (deviceId: string) => void;
}) => {
  const selectedMicrophone: ISelectOption | undefined = useMemo(() => {
    if (callState.selectedInputDevice) {
      return {
        label: callState.selectedInputDevice?.label,
        value: callState.selectedInputDevice?.deviceId,
      };
    }

    const defaultInputDevice = callState.availableInputDevices?.get('default');
    if (defaultInputDevice) {
      return {
        label: defaultInputDevice.label,
        value: defaultInputDevice.deviceId,
      };
    }
  }, [callState.selectedInputDevice, callState.availableInputDevices]);

  const selectedSpeaker: ISelectOption | undefined = useMemo(() => {
    const devices = callState.activeSpeakerDevices
      ? [...callState.activeSpeakerDevices.get().values()]
      : [];
    return devices.length > 0
      ? {
          label: devices[0].label,
          value: devices[0].deviceId,
        }
      : undefined;
  }, [callState.activeSpeakerDevices]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [close]);

  const microphoneOptions: ISelectOption[] = useMemo(
    () =>
      callState.availableInputDevices
        ? [...callState.availableInputDevices.values()].map((device) => ({
            label: device.label,
            value: device.deviceId,
          }))
        : [],
    [callState.availableInputDevices],
  );

  const speakerOptions: ISelectOption[] = useMemo(
    () =>
      callState.availableOutputDevices
        ? [...callState.availableOutputDevices.values()].map((device) => ({
            label: device.label,
            value: device.deviceId,
          }))
        : [],
    [callState.availableOutputDevices],
  );

  return (
    <div
      ref={containerRef}
      className="absolute bg-white border border-neutral-300 rounded-xl p-2 w-75"
    >
      <div className="flex gap-1 items-center mb-1">
        <MicrophoneIcon size={16} color={colors.mainDark} />
        <span className="text-xs font-medium">Microphone</span>
      </div>

      <SingleSelect
        selectedOption={selectedMicrophone}
        handleSelectedOption={(option) => changeMicrophone(option.value)}
        options={microphoneOptions}
        placeholderText="Select microphone"
      />

      <div className="h-px bg-neutral-300 w-full my-2.5" />

      <div className="flex gap-1 items-center mb-1">
        <SpeakerIcon size={16} color={colors.mainDark} />
        <span className="text-xs font-medium">Speaker</span>
      </div>

      <SingleSelect
        selectedOption={selectedSpeaker}
        handleSelectedOption={(option) => changeSpeaker(option.value)}
        options={speakerOptions}
        placeholderText="Select speaker"
      />
    </div>
  );
};
